/**
 * Auth storage utilities for beatly-omni-app.
 * Shape matches beatly-web-app auth storage for compatibility.
 */

import { isEmbedMode } from '@/utils/urlParams';

/** Subset of v2Team.account_manager from GET /app/v2/user (client teams). */
export interface AuthAccountManager {
  name: string;
  email: string;
  picture_url?: string;
  phone?: string;
  slack_url?: string;
  msteams_url?: string;
  whatsapp_url?: string;
}

/** Minimal api-v2 user shape (GET /app/v2/user). Any valid user may log in (clients and influencers). */
export interface AuthUser {
  user_id: string;
  name?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  phone_is_verified?: boolean;
  language?: string;
  want_email_notifications?: boolean;
  want_sms_notifications?: boolean;
  is_client?: boolean;
  is_influencer?: boolean;
  /** Markets where this user runs marketing campaigns (ISO 3166-1 alpha-2). */
  campaign_countries?: string[];
  /** When true (api-v2 staff flag), omni shows Cowork/Chat and related nav. */
  show_cowork?: boolean;
  /** When true (api-v2 staff flag), omni shows Trends and Evolve nav. */
  show_evolve?: boolean;
  team?: {
    name?: string;
    team_id?: string;
    account_manager?: AuthAccountManager;
    /** When true, team can use mention monitoring (api-v2 GET /app/v2/user). */
    has_mention_enabled?: boolean;
    /** When true, team can connect Meta/TikTok ad accounts (api-v2 GET /app/v2/user). */
    has_ad_accounts_enabled?: boolean;
  };
  client?: { company_name?: string };
  [key: string]: unknown;
}

export interface AuthStorage {
  token?: string;
  user?: AuthUser;
  start_url?: string;
  code?: string;
  target?: string;
  language?: string;
}

const AUTH_KEY = 'auth';

/**
 * Where the session lives.
 *
 * - A normal (client / influencer) session lives in `localStorage.auth`, shared by every omni tab — unchanged.
 * - A BO admin session (user_id `admin-…`) and ANY session inside the BO's Omni frame (embed mode) lives in
 *   `sessionStorage.auth`: per frame / per tab, never in localStorage. bo.beatly.com and omni.beatly.com are the same
 *   site, so the frame shares localStorage with the staff member's own omni tabs; an admin session written there used
 *   to replace their own sign-in (empty Team list, placeholder "My Brand" workspace).
 * - An admin-shaped token found in localStorage (left by older builds) is never used: it is removed on read, and the
 *   tab continues as signed out (normal login screen) — the stuck state heals on the next load.
 */

/** True for api-v2 BACK-OFFICE tokens (user_id `admin-<email>`). Payload is decoded, never trusted. */
export function isAdminShapedToken(token: string | null | undefined): boolean {
  if (!token) return false;
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return typeof payload.user_id === 'string' && payload.user_id.startsWith('admin-');
  } catch {
    return false;
  }
}

function parseAuth(raw: string | null): AuthStorage | null {
  if (raw === null) return null;
  try {
    const parsed = JSON.parse(raw) as unknown;
    return parsed && typeof parsed === 'object' ? (parsed as AuthStorage) : {};
  } catch {
    return {};
  }
}

function readSessionAuth(): AuthStorage | null {
  try { return parseAuth(sessionStorage.getItem(AUTH_KEY)); } catch { return null; }
}

function writeSessionAuth(auth: AuthStorage): void {
  try { sessionStorage.setItem(AUTH_KEY, JSON.stringify(auth)); } catch { /* storage unavailable */ }
}

function removeSessionAuth(): void {
  try { sessionStorage.removeItem(AUTH_KEY); } catch { /* storage unavailable */ }
}

/** The shared (localStorage) session — never an admin one: a stale admin session found there is removed. */
function readSharedAuth(): AuthStorage {
  try {
    const auth = parseAuth(localStorage.getItem(AUTH_KEY)) ?? {};
    if (isAdminShapedToken(auth.token)) {
      localStorage.removeItem(AUTH_KEY);
      return {};
    }
    return auth;
  } catch {
    return {};
  }
}

export function getAuthFromStorage(): AuthStorage {
  const own = readSessionAuth();
  if (isEmbedMode()) {
    // The frame's own session (even an empty "signed out" one) wins; without one, a normal user session from the
    // shared storage still works as before. Never an admin session from the shared storage.
    return own ?? readSharedAuth();
  }
  // Outside the frame, sessionStorage only ever holds an admin session handed over in this tab's URL.
  if (own && isAdminShapedToken(own.token)) return own;
  return readSharedAuth();
}

export function updateAuthInStorage(updates: Partial<AuthStorage>): void {
  const updatedAuth = { ...getAuthFromStorage(), ...updates };
  if (isEmbedMode() || isAdminShapedToken(updatedAuth.token)) {
    writeSessionAuth(updatedAuth);
    return;
  }
  // A normal session replaces any admin session this tab carried.
  removeSessionAuth();
  try { localStorage.setItem(AUTH_KEY, JSON.stringify(updatedAuth)); } catch { /* storage unavailable */ }
}

/**
 * End the current session. Inside the BO frame only the frame's own session is cleared (an empty marker, so the
 * frame stays signed out instead of falling back to the staff member's own session in the shared storage).
 */
export function clearAuthFromStorage(): void {
  if (isEmbedMode()) {
    writeSessionAuth({});
    return;
  }
  removeSessionAuth();
  try { localStorage.removeItem(AUTH_KEY); } catch { /* storage unavailable */ }
}

/** Drop only a per-tab (admin / embed) session, leaving the shared user session alone. */
export function clearAdminSessionFromStorage(): void {
  if (isEmbedMode()) writeSessionAuth({});
  else removeSessionAuth();
}

export function getTokenFromStorage(): string | undefined {
  const auth = getAuthFromStorage();
  return auth.token;
}

export function getUserFromStorage(): AuthUser | undefined {
  const auth = getAuthFromStorage();
  return auth.user;
}

/** Decode JWT `team_id` without verifying — used only to detect cross-tab team switches. */
export function teamClaimFromToken(token: string | null | undefined): string | undefined {
  if (!token) return undefined;
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return typeof payload.team_id === 'string' ? payload.team_id : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Another Omni tab reminted the JWT (team select). Shared localStorage already
 * holds the new token; this tab must reload so in-memory React Query / auth
 * state and every team-scoped call (datalake included) see the same claim.
 * Admin / embed sessions live in sessionStorage and are per-tab — ignore them.
 */
export function listenForAuthStorageSync(): () => void {
  if (typeof window === 'undefined') return () => {};
  const onStorage = (e: StorageEvent) => {
    if (e.key !== AUTH_KEY || e.storageArea !== localStorage) return;
    if (isEmbedMode()) return;
    const next = parseAuth(e.newValue);
    const prev = parseAuth(e.oldValue);
    if (!next?.token || isAdminShapedToken(next.token)) return;
    if (teamClaimFromToken(next.token) === teamClaimFromToken(prev?.token)) return;
    window.location.reload();
  };
  window.addEventListener('storage', onStorage);
  return () => window.removeEventListener('storage', onStorage);
}

/**
 * Clear auth storage and redirect to /login. Use from TopNavBar or any component.
 */
export function logout(): void {
  // Signing out also ends this person's phone review codes (they act with this session). Best effort, never blocks.
  const token = getTokenFromStorage();
  const cowork = import.meta.env.VITE_COWORK_API_URL as string | undefined;
  if (token && cowork) { try { void fetch(`${cowork}/v1/review-passes/revoke`, { method: 'POST', keepalive: true, headers: { Authorization: `Bearer ${token}` } }).catch(() => {}); } catch { /* offline */ } }
  clearAuthFromStorage();
  window.location.href = '/login';
}
